export type MoodAudienceType = "general" | "gallery" | "users";

export type MoodLike = {
  published?: boolean;
  users?: string[];
  audienceType?: MoodAudienceType;
  galleryId?: string;
  galleryIds?: string[];
};

export type MoodVisibilityContext = {
  userId?: string;
  galleryId?: string;
  isAdmin?: boolean;
  includeUnpublished?: boolean;
  includeAssignedUnpublished?: boolean;
};

export type MoodEntry<T = MoodLike> = {
  mood: T;
  index: number;
  audienceType: MoodAudienceType;
};

const normalizeUsers = (users: unknown): string[] => {
  if (!Array.isArray(users)) return [];
  return users.map((id) => String(id));
};

const normalizeGalleryIds = (mood: MoodLike): string[] => {
  const galleryIds = Array.isArray(mood.galleryIds) ? mood.galleryIds.map((id) => String(id)) : [];
  if (typeof mood.galleryId === "string" && mood.galleryId) {
    galleryIds.push(String(mood.galleryId));
  }
  return Array.from(new Set(galleryIds.filter(Boolean)));
};

export const getMoodAudienceType = (mood: MoodLike): MoodAudienceType => {
  if (mood.audienceType === "gallery" || mood.audienceType === "users" || mood.audienceType === "general") {
    return mood.audienceType;
  }
  if (normalizeGalleryIds(mood).length > 0) return "gallery";
  const users = normalizeUsers(mood.users);
  return users.length > 0 ? "users" : "general";
};

export const isMoodVisible = (mood: MoodLike, context: MoodVisibilityContext): boolean => {
  if (!mood) return false;

  const audienceType = getMoodAudienceType(mood);
  const includeUnpublished = context.includeUnpublished === true;
  const includeAssignedUnpublished = context.includeAssignedUnpublished === true;
  const isAdmin = context.isAdmin === true;

  if (audienceType === "gallery") {
    if (!context.galleryId) return false;
    const matchesGallery = normalizeGalleryIds(mood).includes(String(context.galleryId));
    if (!matchesGallery) return false;
    if (includeUnpublished || isAdmin) return true;
    if (mood.published === true) return true;
    return includeAssignedUnpublished;
  }

  if (audienceType === "users") {
    if (!context.userId) return false;
    const users = normalizeUsers(mood.users);
    const assignedToUser = users.includes(String(context.userId));
    if (!assignedToUser) return false;
    if (includeUnpublished || isAdmin) return true;
    if (mood.published === true) return true;
    return includeAssignedUnpublished;
  }

  if (includeUnpublished || isAdmin) return true;
  return mood.published === true;
};

const getMoodSortPriority = (audienceType: MoodAudienceType): number => {
  if (audienceType === "gallery") return 0;
  if (audienceType === "users") return 1;
  return 2;
};

export const getVisibleMoodEntries = <T extends MoodLike>(
  moods: T[] | undefined,
  context: MoodVisibilityContext
): MoodEntry<T>[] => {
  if (!Array.isArray(moods)) return [];

  const entries: MoodEntry<T>[] = moods
    .map((mood, index) => ({
      mood,
      index,
      audienceType: index === 0 ? "general" as const : getMoodAudienceType(mood),
    }))
    .filter((entry) => entry.index === 0 || isMoodVisible(entry.mood, context));

  entries.sort((a, b) => {
    if (a.index === 0 || b.index === 0) {
      if (a.index === b.index) return 0;
      return a.index === 0 ? -1 : 1;
    }
    const priorityDiff = getMoodSortPriority(a.audienceType) - getMoodSortPriority(b.audienceType);
    if (priorityDiff !== 0) return priorityDiff;
    return a.index - b.index;
  });

  return entries;
};

export const resolveActiveMoodIndex = (
  moods: MoodLike[] | undefined,
  currentMoodIndex: number | undefined,
  context: MoodVisibilityContext
): number => {
  if (Array.isArray(moods) && Number.isInteger(currentMoodIndex)) {
    const safeCurrentMoodIndex = Number(currentMoodIndex);
    if (safeCurrentMoodIndex >= 0 && safeCurrentMoodIndex < moods.length) {
      // Persisted gallery mood must win when it points to an existing mood.
      return safeCurrentMoodIndex;
    }
  }

  const visibleEntries = getVisibleMoodEntries(moods, context);
  if (visibleEntries.length === 0) return 0;

  return visibleEntries[0].index;
};
